import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../common/components/RTKQErrorCard";
import { useGetOrderQuery } from "../api/orderApi";
import { useGetCitiesQuery } from "../../checkout/api/checkoutApi";
import { useClearCartMutation } from "../../cart/api/cartApi";
import type { ThemeVariables } from "../../../types";
import { skipToken } from "@reduxjs/toolkit/query";
import type { RTKQueryError } from "../../../common/components/RTKQErrorCard";

import OrderDetailsDisplay from "./OrderDetailsDisplay";
import OrderItemsList from "./OrderItemsList";
import OrderTotalsDisplay from "./OrderTotalsDisplay";
import InvalidOrderMessage from "./InvalidOrderMessage";

const OrderPageContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const paymentMethodParam = queryParams.get("payment_method");
  const orderIdStr = queryParams.get("order_id");
  const orderKey = queryParams.get("order_key");
  const billingEmail = queryParams.get("billing_email") || undefined;
  const shouldClearCart = queryParams.get("clear_cart") === "1";

  const orderId = orderIdStr ? parseInt(orderIdStr, 10) : null;

  const {
    data: citiesData,
    isLoading: isCitiesLoading,
    error: citiesError,
  } = useGetCitiesQuery();

  const shouldFetchExistingOrder = orderId !== null && !!orderKey;
  const {
    data: orderData,
    error: getOrderError,
    isLoading: getOrderLoading,
    isError: isGetOrderError,
  } = useGetOrderQuery(
    shouldFetchExistingOrder
      ? { orderId: orderId!, key: orderKey!, billingEmail }
      : skipToken
  );

  const [clearCart] = useClearCartMutation();

  const isLoading =
    !translationsReady ||
    isCitiesLoading ||
    (shouldFetchExistingOrder && getOrderLoading);

  const isError =
    !!citiesError || (shouldFetchExistingOrder && isGetOrderError);
  const displayError = citiesError || (isGetOrderError ? getOrderError : null);

  const themeVars = window.themeVariables as ThemeVariables | undefined;
  const shopPageUrl = themeVars?.shopPageUrl || "/shop";

  useEffect(() => {
    if (shouldClearCart) {
      console.log("[OrderPage] clear_cart flag detected. Clearing cart.");
      clearCart()
        .unwrap()
        .then(() => {
          console.log("[OrderPage] Cart cleared successfully.");
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete("clear_cart");
          navigate(`${location.pathname}?${newSearchParams.toString()}`, {
            replace: true,
          });
        })
        .catch((error) => {
          console.error("[OrderPage] Failed to clear cart:", error);
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete("clear_cart");
          navigate(`${location.pathname}?${newSearchParams.toString()}`, {
            replace: true,
          });
        });
    }
  }, [
    shouldClearCart,
    clearCart,
    navigate,
    location.search,
    location.pathname,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [orderId, orderKey]);

  const renderLoadingState = () => (
    <div className="order-page-loading">
      <div className="order-details-skeleton">
        <h2>
          <Skeleton width={200} height={24} />
        </h2>
        <Skeleton count={3} height={18} style={{ marginBottom: "0.5rem" }} />
      </div>
      <div className="address-section-skeleton">
        <div className="address-block-skeleton">
          <h3>
            <Skeleton width={150} height={20} />
          </h3>
          <Skeleton count={4} height={16} />
        </div>
        <div className="address-block-skeleton">
          <h3>
            <Skeleton width={150} height={20} />
          </h3>
          <Skeleton count={4} height={16} />
        </div>
      </div>
      <div className="order-items-skeleton">
        <h3>
          <Skeleton width={180} height={22} />
        </h3>
        {[...Array(2)].map((_, i) => (
          <div className="order-item-row-skeleton" key={i}>
            <Skeleton width={60} height={60} circle />
            <div className="item-details-skeleton">
              <Skeleton width="70%" height={18} />
              <Skeleton width="40%" height={14} />
            </div>
            <Skeleton width={80} height={18} />
          </div>
        ))}
      </div>
      <div className="order-totals-skeleton">
        <h3>
          <Skeleton width={120} height={20} />
        </h3>
        <Skeleton count={4} height={18} />
      </div>
    </div>
  );

  const renderContent = () => {
    if (!shouldFetchExistingOrder) {
      return (
        <InvalidOrderMessage
          messageKey="order.missingParamsError"
          defaultMessage="Order details could not be retrieved. Required information is missing."
        />
      );
    }
    if (isLoading) {
      return renderLoadingState();
    }
    if (isError) {
      const errorTitleKey = getOrderError
        ? "order.loadErrorTitle"
        : "checkout.citiesLoadErrorTitle";
      const defaultErrorTitle = getOrderError
        ? "Could not load order details"
        : "Could not load address information";
      return (
        <div className="order-error-container">
          <RTKQErrorCard
            error={displayError as RTKQueryError | null}
            title={t(errorTitleKey, defaultErrorTitle)}
          />
          <a href={shopPageUrl} className="button shop-link">
            {t("order.backToShop", "Back to Shop")}
          </a>
        </div>
      );
    }
    if (!orderData) {
      return (
        <InvalidOrderMessage
          messageKey="order.notFoundError"
          defaultMessage="Order details could not be found. It might be invalid or you may need to log in."
        />
      );
    }

    return (
      <div className="order-confirmation-content">
        <OrderDetailsDisplay
          orderId={orderData.id}
          status={orderData.status}
          billingAddress={orderData.billingAddress}
          shippingAddress={orderData.shippingAddress}
          cities={citiesData}
          orderMethod={paymentMethodParam}
        />
        <OrderItemsList items={orderData.items} />
        <OrderTotalsDisplay
          totals={orderData.totals}
          coupons={orderData.coupons}
        />
      </div>
    );
  };

  return (
    <div className="order-page-container">
      <h1>{t("order.title", "Order Information")}</h1>
      <div className="order-page-content">{renderContent()}</div>
    </div>
  );
};

export default OrderPageContainer;
